import { DefaultProfile } from "@/components/DefaultProfile";
import { Column } from "@/components/ui/flex";
import { Image } from "@/components/ui/Image";
import { resizeImage, validateImageFile } from "@/lib/image";
import { useState } from "react";
import { Button } from "../ui/button";

interface ProfilePictureSelectorProps {
  profilePicture: string | null | undefined;
  onProfilePictureChange: (
    profilePicture: string | null,
  ) => void | Promise<void>;
  resizeSize?: number | [number, number];
}

export function ProfilePictureSelector({
  profilePicture,
  onProfilePictureChange,
  resizeSize = 128,
}: ProfilePictureSelectorProps) {
  const fileInputId = `profile-picture-upload-${Math.random().toString(36).substr(2, 9)}`;
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError(null);
    setIsProcessing(true);

    try {
      const validation = validateImageFile(file);
      if (!validation.isValid) {
        setError(validation.error || "Invalid file");
        return;
      }

      const result = await resizeImage(file, resizeSize, 0.9);

      await onProfilePictureChange(result);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to process image";
      setError(errorMessage);
      console.error("Error processing image:", err);
    } finally {
      setIsProcessing(false);
      e.target.value = "";
    }
  };

  return (
    <div className="flex items-center gap-4">
      {profilePicture ? (
        <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-full">
          <Image
            src={profilePicture}
            alt="Cover Image"
            className="h-full w-full object-cover"
          />
        </div>
      ) : (
        <DefaultProfile size={64} className="flex-shrink-0" />
      )}

      <div className="flex-1">
        <p className="text-xs text-gray-500">
          {isProcessing
            ? "Processing image..."
            : profilePicture
              ? Array.isArray(resizeSize)
                ? `Profile Picture (${resizeSize[0]}×${resizeSize[1]})`
                : `Profile Picture (${resizeSize}×${resizeSize})`
              : "Cover Image"}
        </p>
        {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
      </div>

      <Column className="gap-2">
        <input
          type="file"
          id={fileInputId}
          accept="image/*"
          className="hidden"
          onChange={handleFileChange}
        />
        <Button
          onClick={() => document.getElementById(fileInputId)?.click()}
          disabled={isProcessing}
        >
          {isProcessing
            ? "Processing..."
            : profilePicture
              ? "Change"
              : "Upload"}
        </Button>
        {profilePicture && (
          <Button
            onClick={() => onProfilePictureChange(null)}
            disabled={isProcessing}
            variant="destructive"
          >
            Remove
          </Button>
        )}
      </Column>
    </div>
  );
}
